import type {
	LinksFunction,
	LoaderFunction,
	MetaFunction,
} from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';

import { Banner } from '~/components/atoms/Banner/index.tsx';
import divider from '~/components/atoms/Divider/index.tsx';

import contentStylesUrl from '~/styles/content/content.css?url';

export const links: LinksFunction = () => [
	{
		rel: 'stylesheet',
		href: contentStylesUrl,
	},
];

export const meta: MetaFunction = () => [
	{
		title: 'GList features and functionality',
	},
];

type TLoaderData = {
	divider: string;
};

export const loader: LoaderFunction = async (): Promise<TLoaderData> => {
	const theDivider = divider();
	return {
		divider: theDivider,
	};
};

export default function Pricing(): React.ReactNode {
	const { divider } = useLoaderData<TLoaderData>();
	return (
		<main id="main">
			<Banner title="Features" />
			<div
				className="divider white"
				role="img"
				aria-label="Mountain-scape divider"
				aria-hidden="true"
				dangerouslySetInnerHTML={{ __html: divider }}
			/>
			<section className="container content-container-alt small-container">
				<h2>Basic Features (Available for Free Tier):</h2>
				<ul>
					<li>
						<strong>List Creation:</strong> Users can create
						multiple lists for different occasions like Christmas,
						birthdays, anniversaries, etc.
					</li>
					<li>
						<strong>Group Creation:</strong> Ability to create one
						group for organizing a specific event or occasion. Each
						group can accommodate up to 5 people.
					</li>
					<li>
						<strong>Invite Members:</strong> Users can invite family
						or friends to join their created groups/lists via email
						or a shareable link.
					</li>
					<li>
						<strong>List Management:</strong> Add, edit, or delete
						items within the lists. Users can specify gift
						preferences, descriptions, and links if needed.
					</li>
					<li>
						<strong>Account Management:</strong> Users can manage
						their personal account settings, including profile
						details, password changes, and email preferences.
					</li>
					<li>
						<strong>Group Member Management:</strong> The user who
						created the group can manage group members, including
						inviting new members and removing existing ones.
					</li>
				</ul>

				<h2>Additional Features (Available for Premium Tiers):</h2>
				<ul>
					<li>
						<strong>Multiple Groups:</strong> Premium tiers offer
						the ability to create and manage multiple groups for
						various occasions simultaneously.
					</li>
					<li>
						<strong>Expanded Group Capacity:</strong> Higher-tier
						plans allow for larger group sizes beyond the 5-person
						limit of the free tier.
					</li>
					<li>
						<strong>Enhanced Collaboration Tools:</strong> Enable
						real-time collaboration features such as live chat,
						comments, or discussion boards within the lists/groups.
					</li>
					<li>
						<strong>Advanced List Customization:</strong> Additional
						options for list customization, including adding images,
						setting priority levels, or categorizing items.
					</li>
					<li>
						<strong>Advanced Security and Privacy Settings:</strong>{' '}
						More robust control over privacy settings, allowing
						users to set permissions on who can view, edit, or
						contribute to their lists.
					</li>
					<li>
						<strong>Integration with E-commerce Platforms:</strong>{' '}
						Premium users can integrate with e-commerce platforms,
						enabling direct purchasing from the list interface.
					</li>
					<li>
						<strong>Advanced Analytics and Insights:</strong> Access
						detailed analytics on list activity, popular items, and
						member engagement for better planning and
						decision-making.
					</li>
					<li>
						<strong>Priority Support:</strong> Premium users receive
						priority customer support for any issues or inquiries
						they may have.
					</li>
				</ul>
			</section>
		</main>
	);
}
